import { useState, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import { useUI } from "@/context/UIContext";
import { dateToStr, minutesToLabel, formatDuration, eventsForDate, strToDate } from "@/lib/timeUtils";
import { Icon } from "@/lib/icons";
import { MapPin } from "lucide-react";
import { motion } from "framer-motion";

function groupLabel(ds) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = strToDate(ds);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 7) return "This Week";
  if (diff < 14) return "Next Week";
  return "Later";
}

export function UpcomingPage() {
  const { events, categories, eventColor, settings } = useApp();
  const { openEventModal } = useUI();
  const format = settings?.time_format || "12";
  const [catFilter, setCatFilter] = useState("all");

  const items = useMemo(() => {
    const out = [];
    const base = new Date(); base.setHours(0, 0, 0, 0);
    for (let i = 0; i < 30; i++) {
      const d = new Date(base); d.setDate(base.getDate() + i);
      const ds = dateToStr(d);
      eventsForDate(events, ds).forEach((e) => out.push({ ...e, _date: ds }));
    }
    return out.filter((e) => catFilter === "all" || e.category_id === catFilter);
  }, [events, catFilter]);

  const groups = {};
  items.forEach((e) => { const g = groupLabel(e._date); (groups[g] = groups[g] || []).push(e); });
  const order = ["Today", "Tomorrow", "This Week", "Next Week", "Later"];

  return (
    <div className="h-full overflow-y-auto p-4 lg:p-6">
      <h2 className="font-display text-2xl font-extrabold mb-1" style={{ color: "var(--text-primary)" }}>Upcoming</h2>
      <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>Your next 30 days at a glance</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setCatFilter("all")} className="px-3.5 py-1.5 rounded-full text-xs font-medium" style={{ background: catFilter === "all" ? "var(--accent-primary)" : "var(--bg-card)", color: catFilter === "all" ? "#fff" : "var(--text-secondary)", border: "1px solid var(--border-subtle)" }} data-testid="upcoming-filter-all">All</button>
        {categories.map((c) => (
          <button key={c.id} onClick={() => setCatFilter(c.id)} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium" style={{ background: catFilter === c.id ? c.color : "var(--bg-card)", color: catFilter === c.id ? "#fff" : "var(--text-secondary)", border: `1px solid ${catFilter === c.id ? c.color : "var(--border-subtle)"}` }}>
            <Icon name={c.icon} size={12} /> {c.name}
          </button>
        ))}
      </div>

      <div className="space-y-6 max-w-3xl">
        {order.filter((g) => groups[g]).map((g) => (
          <div key={g}>
            <h3 className="text-xs font-semibold uppercase tracking-widest mb-2.5" style={{ color: "var(--text-muted)" }}>{g}</h3>
            <div className="space-y-2">
              {groups[g].map((e, i) => {
                const color = eventColor(e);
                return (
                  <motion.button key={e.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                    onClick={() => openEventModal(e)}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-transform hover:scale-[1.005]"
                    style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderLeft: `3px solid ${color}` }} data-testid="upcoming-event">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `color-mix(in srgb, ${color} 22%, transparent)` }}>
                      <Icon name={e.icon || "Calendar"} size={18} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: "var(--text-primary)" }}>{e.title}</p>
                      <p className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>{strToDate(e._date).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {minutesToLabel(e.start_minutes, format)}{e.location ? ` · ${e.location}` : ""}</p>
                    </div>
                    <span className="text-[10px] font-mono px-2 py-1 rounded-md shrink-0" style={{ background: "var(--bg-timeline-track)", color: "var(--text-secondary)" }}>{formatDuration(e.end_minutes - e.start_minutes)}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No upcoming events.</p>}
      </div>
    </div>
  );
}
